import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ChevronDown,
  ChevronRight,
  Building2,
  TrendingUp,
  FileText,
  Banknote,
  Activity,
  Shield,
  Bell,
  LogOut,
} from "lucide-react";
import { useUser } from "@/context/useUser";
import { getUserInfo } from "@/lib/storeManager";

const FinEaseAdminLayout = () => {
  const { isAuthenticated, logout } = useUser();
  const [userInfo, setUserInfo] = useState<any>(null);
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    products: true,
    nps: true,
  });

  const toggleSection = (section: "products" | "nps") => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: "Dashboard",
      path: "/dashboard",
    },
    {
      icon: Users,
      label: "User Management",
      path: "/user-management",
    },
  ];

  const productItems = [
    {
      label: "NPS",
      icon: Building2,
      children: [
        {
          label: "NPS Users",
          path: "/user-list",
        },
        {
          label: "Payment Entry",
          path: "/payment-entry",
        },
        {
          label: "Settlements",
          path: "/settlements",
        },
      ],
    },
    {
      label: "IPO",
      icon: TrendingUp,
      path: "/products/ipo",
    },
    {
      label: "Bonds",
      icon: FileText,
      path: "/products/bonds",
    },
    {
      label: "Fixed Deposits",
      icon: Banknote,
      path: "/products/fixed-deposits",
    },
  ];

  const systemItems = [
    {
      icon: Activity,
      label: "Activity Logs",
      path: "/activity-log",
    },
    {
      icon: Shield,
      label: "Role Management",
      path: "/system/role-management",
    },
  ];

  useEffect(() => {
    const userInfoData = getUserInfo();
    setUserInfo(userInfoData);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="flex w-64 flex-col border-r border-gray-200 bg-white">
        {/* Logo Section */}
        <div className="border-b border-gray-200 p-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img
              src="/assets/NPSLogo.png"
              alt="logo"
              className="h-10 w-auto max-w-full"
            />
          </Link>
        </div>

        {/* Super Admin Badge */}
        <div className="border-b border-gray-200 p-4">
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <div className="text-sm font-semibold text-green-700">
              Super Admin
            </div>
            <div className="mt-1 text-xs text-green-600">Full Access</div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4">
          {/* Main Menu Items */}
          <div className="space-y-1 px-2">
            {menuItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                    active
                      ? "bg-gray-100 text-teal-600"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* PRODUCTS Section */}
          <div className="mt-6 px-2">
            <div className="mb-2 px-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                PRODUCTS
              </span>
            </div>
            <div className="space-y-1">
              {productItems.map(item => {
                if (item.children) {
                  const isNpsExpanded = expandedSections.nps;
                  const hasActiveChild = item.children.some(child =>
                    isActive(child.path)
                  );
                  const Icon = item.icon;

                  return (
                    <div key={item.label}>
                      <button
                        onClick={() => toggleSection("nps")}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 transition-colors ${
                          hasActiveChild
                            ? "bg-gray-100 text-teal-600"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5" />
                          <span className="text-sm font-medium">
                            {item.label}
                          </span>
                        </div>
                        {isNpsExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      {isNpsExpanded && (
                        <div className="ml-8 mt-1 space-y-1">
                          {item.children.map(child => {
                            const active = isActive(child.path);
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`block rounded-lg px-3 py-2 transition-colors ${
                                  active
                                    ? "bg-teal-50 font-medium text-teal-600"
                                    : "text-gray-600 hover:bg-gray-50"
                                }`}
                              >
                                <span className="text-sm">{child.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  const Icon = item.icon;
                  const active = isActive(item.path || "");
                  return (
                    <Link
                      key={item.path}
                      to={item.path || ""}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                        active
                          ? "bg-gray-100 text-teal-600"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  );
                }
              })}
            </div>
          </div>

          {/* SYSTEM Section */}
          <div className="mt-6 px-2">
            <div className="mb-2 px-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                SYSTEM
              </span>
            </div>
            <div className="space-y-1">
              {systemItems.map(item => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                      active
                        ? "bg-gray-100 text-teal-600"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Bottom Profile Section */}
        <div className="space-y-3 border-t border-gray-200 p-4">
          {/* Profile Box */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-500 font-semibold text-white">
              SA
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-800">
                Super Admin
              </div>
              <div className="truncate text-xs text-gray-500">
                {userInfo?.email}
              </div>
            </div>
            <button className="shrink-0 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700">
              <Bell className="h-5 w-5" />
            </button>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-red-600 transition-colors hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default FinEaseAdminLayout;
